import { LocalSaveManager } from './LocalSaveManager';
import { logger } from '../Analytics/Logger';

const DEVICE_ID_NAMESPACE = 'system';
const DEVICE_ID_KEY = 'deviceId';
const DEVICE_ID_EXPIRATION_YEARS = 10;

/**
 * Manages device identification for save reconciliation
 * Stores a unique device ID using LocalSaveManager to identify saves from this device
 */
export class DeviceIdManager {
  private static deviceId: string | null = null;

  /**
   * Get or create the device ID for this device
   * Format: deviceId_UUID
   */
  static getDeviceId(): string {
    if (DeviceIdManager.deviceId) {
      return DeviceIdManager.deviceId;
    }

    const saveObject = LocalSaveManager.loadSimpleData<string>(DEVICE_ID_NAMESPACE, DEVICE_ID_KEY);
    let deviceId = saveObject?.data;

    if (!deviceId) {
      // Generate a new device ID
      deviceId = `deviceId_${DeviceIdManager.generateUUID()}`;
      
      // Store in cookie with long expiration (10 years)
      const expirationDate = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000 * DEVICE_ID_EXPIRATION_YEARS);
      LocalSaveManager.saveSimpleData(DEVICE_ID_NAMESPACE, DEVICE_ID_KEY, deviceId, expirationDate);

      logger.info(`[DeviceIdManager] Generated new device ID: ${deviceId}`);
    } else {
      logger.debug(`[DeviceIdManager] Using existing device ID: ${deviceId}`);
    }

    DeviceIdManager.deviceId = deviceId;
    return deviceId;
  }

  /**
   * Generate a UUID v4
   * Uses crypto.randomUUID() if available, falls back to Math.random() based approach
   */
  private static generateUUID(): string {
    try {
      // Use crypto.randomUUID if available (requires secure context/HTTPS)
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
      }
    } catch (err) {
      // Fallback to manual implementation if crypto fails
    }
    
    // Fallback: Simple UUID v4 implementation using Math.random()
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}
